import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { JOB_STATUS_OPTIONS } from '@jobos/shared/constants/job-statuses';
import { JOB_CARD_STATUS_OPTIONS, JOB_INVOICE_STATUS_OPTIONS } from '@jobos/shared';
import { useProjectTypes } from '@/hooks/useConfigurableLists';
import { IncludeArchivedCheckbox } from './IncludeArchivedCheckbox';
import {
  PRESET_CHIPS,
  type DatePreset,
  type DrawerDraft,
  type PresetChip,
} from '../../hooks/useJobListFilters';
import type { JobCardStatus, JobInvoiceStatus } from '@jobos/shared/types/job';

const DATE_FIELD_OPTIONS = [
  { value: 'statusDate', label: 'Status Date (auto)' },
  { value: 'noticeDate', label: 'Notice Date' },
  { value: 'estimateSendDate', label: 'Estimate Send Date' },
  { value: 'startDate', label: 'Start Date' },
  { value: 'completedDate', label: 'Completed Date' },
  { value: 'lastInvoicedDate', label: 'Last Invoiced Date' },
  { value: 'createdAt', label: 'Created Date' },
];

interface FilterDrawerProps {
  isOpen: boolean;
  isAnimating: boolean;
  onClose: () => void;
  onApply: (draft: DrawerDraft) => void;
  onClear: () => void;
  onManageProfiles: () => void;
  showArchived: boolean | 'only';
  showDeleted: boolean;
  selectedStatuses: string[];
  onStatusesChange: (statuses: string[]) => void;
  selectedJobCardStatuses: string[];
  onJobCardStatusesChange: (statuses: string[]) => void;
  selectedInvoiceStatuses: string[];
  onInvoiceStatusesChange: (statuses: string[]) => void;
  selectedClients: string[];
  onClientsChange: (clients: string[]) => void;
  selectedProjectTypes: string[];
  onProjectTypesChange: (types: string[]) => void;
  availableClients: string[];
  searchText: string;
  dateField: string;
  dateFrom: string;
  dateTo: string;
  datePreset: DatePreset;
  onDateFilterChange: (field: string, from: string, to: string) => void;
  onDatePreset: (preset: DatePreset) => void;
  onClearDateFilter: () => void;
}

export function FilterDrawer({
  isOpen,
  isAnimating,
  onClose,
  onApply,
  onClear,
  onManageProfiles,
  showArchived,
  showDeleted,
  selectedStatuses,
  onStatusesChange,
  selectedJobCardStatuses,
  onJobCardStatusesChange,
  selectedInvoiceStatuses,
  onInvoiceStatusesChange,
  selectedClients,
  onClientsChange,
  selectedProjectTypes,
  onProjectTypesChange,
  availableClients,
  searchText,
  dateField,
  dateFrom,
  dateTo,
  datePreset,
  onDateFilterChange,
  onDatePreset,
  onClearDateFilter,
}: FilterDrawerProps) {
  const { data: projectTypes } = useProjectTypes();

  // Search and visibility apply live from the toolbar, so the drawer edits its own
  // copy and only hands it back on Apply — like the rest of the drawer's filters.
  const [draftSearch, setDraftSearch] = useState(searchText);
  const [draftArchived, setDraftArchived] = useState(showArchived);
  const [draftDeleted, setDraftDeleted] = useState(showDeleted);

  // Re-seed on open (drops drafts abandoned by closing without applying) and whenever
  // the applied filters change underneath the drawer (Clear Filters, profile switch).
  useEffect(() => {
    if (!isOpen) return;
    setDraftSearch(searchText);
    setDraftArchived(showArchived);
    setDraftDeleted(showDeleted);
  }, [isOpen, searchText, showArchived, showDeleted]);

  // A full-height sheet over a scrollable page: without this, dragging inside the
  // sheet scrolls the job list underneath it on iOS. Declared above the early
  // return below — hooks cannot run conditionally.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasDateFilter = dateFrom || dateTo;

  const handleApply = () =>
    onApply({ search: draftSearch, showArchived: draftArchived, showDeleted: draftDeleted });

  const applyPreset = (chip: PresetChip) => {
    chip.apply({
      setSelectedJobCardStatuses: (next) => onJobCardStatusesChange(next as JobCardStatus[]),
      setSelectedInvoiceStatuses: (next) => onInvoiceStatusesChange(next as JobInvoiceStatus[]),
    });
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          isAnimating ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      <div
        data-testid="filter-drawer-panel"
        // Full-bleed sheet on a phone; the 20rem rail from sm up, as before.
        className={`fixed inset-y-0 left-0 w-full sm:w-80 bg-white shadow-xl z-50 transition-transform duration-300 ease-in-out ${
          isAnimating ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Filter Jobs</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="min-h-[44px] min-w-[44px]"
                aria-label="Close filters"
              >
                ✕
              </Button>
            </div>
          </div>

          <div className="flex-1 px-6 py-4 overflow-y-auto overscroll-contain">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by job code or address..."
                    value={draftSearch}
                    onChange={(e) => setDraftSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
                    className="pl-9 text-sm"
                  />
                </div>
              </div>

              <MultiSelect
                label="Status"
                options={JOB_STATUS_OPTIONS}
                selected={selectedStatuses}
                onChange={onStatusesChange}
                placeholder="Select statuses..."
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900">Quick Filters</label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_CHIPS.map((chip) => {
                    const isActive = chip.isActive({
                      selectedJobCardStatuses,
                      selectedInvoiceStatuses,
                    });
                    return (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() => applyPreset(chip)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          isActive
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {chip.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <MultiSelect
                label="Job Card Status"
                options={JOB_CARD_STATUS_OPTIONS}
                selected={selectedJobCardStatuses}
                onChange={onJobCardStatusesChange}
                placeholder="Select job card statuses..."
              />

              <MultiSelect
                label="Invoice Status"
                options={JOB_INVOICE_STATUS_OPTIONS}
                selected={selectedInvoiceStatuses}
                onChange={onInvoiceStatusesChange}
                placeholder="Select invoice statuses..."
              />

              <MultiSelect
                label="Client"
                options={availableClients}
                selected={selectedClients}
                onChange={onClientsChange}
                placeholder="Select clients..."
              />

              <MultiSelect
                label="Project Type"
                options={projectTypes}
                selected={selectedProjectTypes}
                onChange={onProjectTypesChange}
                placeholder="Select project types..."
              />

              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-900">Date Range</label>

                <select
                  value={dateField}
                  onChange={(e) => onDateFilterChange(e.target.value, dateFrom, dateTo)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  {DATE_FIELD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                <div className="flex gap-2">
                  {(['last7', 'last30', 'last90'] as DatePreset[]).map((preset) => {
                    const labels: Record<string, string> = {
                      last7: 'Last 7 days',
                      last30: 'Last 30 days',
                      last90: 'Last 90 days',
                    };
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => onDatePreset(preset)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          datePreset === preset
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {labels[preset]}
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">From</label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => onDateFilterChange(dateField, e.target.value, dateTo)}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">To</label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => onDateFilterChange(dateField, dateFrom, e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>

                {hasDateFilter && (
                  <button
                    type="button"
                    onClick={onClearDateFilter}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear date filter
                  </button>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900">Visibility</label>
                <select
                  value={draftDeleted ? 'deleted' : draftArchived === 'only' ? 'archived' : draftArchived ? 'all' : 'active'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'deleted') {
                      setDraftDeleted(true);
                      setDraftArchived(false);
                    } else if (val === 'archived') {
                      setDraftDeleted(false);
                      setDraftArchived('only');
                    } else if (val === 'all') {
                      setDraftDeleted(false);
                      setDraftArchived(true);
                    } else {
                      setDraftDeleted(false);
                      setDraftArchived(false);
                    }
                  }}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="active">Active Jobs Only</option>
                  <option value="all">All Jobs (incl. Archived)</option>
                  <option value="archived">Archived Only</option>
                  <option value="deleted">Deleted Only</option>
                </select>

                <IncludeArchivedCheckbox
                  showArchived={draftArchived}
                  showDeleted={draftDeleted}
                  onArchivedChange={setDraftArchived}
                />
              </div>
            </div>
          </div>

          {/* Secondary actions share a row so Apply — the one that closes the job —
              stays last and lowest, where a thumb rests. */}
          <div
            data-testid="filter-drawer-footer"
            // calc, not a bare env(): the inset is 0 on a desktop or a non-notched
            // phone, and a bare env() would override py-4 and leave the buttons
            // flush against the bottom edge.
            className="px-6 py-4 border-t space-y-3 pb-[calc(1rem+env(safe-area-inset-bottom))]"
          >
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 min-h-[44px]" onClick={onClear}>
                Clear
              </Button>
              <Button variant="outline" className="flex-1 min-h-[44px]" onClick={onManageProfiles}>
                Profiles
              </Button>
            </div>
            <Button className="w-full min-h-[44px]" onClick={handleApply}>
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
